/**
 * 🐋 Role Manager - Cetalingua FM Admin Panel
 * 
 * Handles user role checking and management
 * 
 * ROLES:
 * - admin: Full access to approve/manage everything
 * - advertiser: Whale watching companies
 * - station: Hydrophone station operators (placeholder)
 * - station_advertiser: Combined station + advertiser access
 */

import { doc, getDoc, setDoc, updateDoc } from 'firebase/firestore';
import { db } from '../firebase-config';

// Valid roles
export const VALID_ROLES = ['admin', 'advertiser', 'station', 'station_advertiser'];

/**
 * Get user's role from Firestore
 * @param {string} userId - User ID
 * @returns {Promise<string|null>} User role or null
 */
export async function getUserRole(userId) {
  try {
    if (!userId) return null;
    
    const userDocRef = doc(db, 'users', userId);
    const userDoc = await getDoc(userDocRef);
    
    if (!userDoc.exists()) {
      console.log('User document does not exist');
      return null;
    }
    
    const userData = userDoc.data();
    return userData.role || null;
  } catch (error) {
    console.error('Error getting user role:', error);
    return null;
  }
}

/**
 * Set user's role in Firestore
 * @param {string} userId - User ID
 * @param {string} role - Role to set
 */
export async function setUserRole(userId, role) {
  try {
    if (!userId || !role) {
      throw new Error('userId and role are required');
    }
    
    if (!VALID_ROLES.includes(role)) {
      throw new Error(`Invalid role. Must be one of: ${VALID_ROLES.join(', ')}`);
    }
    
    const userDocRef = doc(db, 'users', userId);
    await updateDoc(userDocRef, { 
      role,
      updatedAt: new Date()
    });
    
    console.log(`Role set to ${role} for user ${userId}`);
  } catch (error) {
    console.error('Error setting user role:', error);
    throw error;
  }
}

/**
 * Check if user is an admin
 */
export async function isAdmin(userId) {
  const role = await getUserRole(userId);
  return role === 'admin';
}

/**
 * Check if user is an advertiser (whale watching company)
 */
export async function isAdvertiser(userId) {
  const role = await getUserRole(userId);
  return role === 'advertiser' || role === 'station_advertiser';
}

/**
 * Check if user is a station operator
 */
export async function isStation(userId) {
  const role = await getUserRole(userId);
  return role === 'station' || role === 'station_advertiser';
}

/**
 * Check if user has combined station + advertiser role
 */
export async function isStationAdvertiser(userId) {
  const role = await getUserRole(userId);
  return role === 'station_advertiser';
}

/**
 * Get user's full profile from Firestore
 * @param {string} userId - User ID
 * @returns {Promise<Object|null>}
 */
export async function getUserProfile(userId) {
  try {
    if (!userId) return null;
    
    const userDocRef = doc(db, 'users', userId);
    const userDoc = await getDoc(userDocRef);
    
    if (!userDoc.exists()) {
      return null;
    }
    
    return {
      id: userDoc.id,
      ...userDoc.data()
    };
  } catch (error) {
    console.error('Error getting user profile:', error);
    return null;
  }
}

/**
 * Create initial user document (used during signup)
 * @param {string} userId - User ID
 * @param {Object} userData - User data
 */
export async function createUserDocument(userId, userData) {
  try {
    if (!userId) {
      throw new Error('userId is required');
    }
    
    const userDocRef = doc(db, 'users', userId);
    const userDoc = await getDoc(userDocRef);
    
    // Don't overwrite if document already exists
    if (userDoc.exists()) {
      console.log('User document already exists');
      return;
    }
    
    // Create new user document
    await setDoc(userDocRef, {
      ...userData,
      createdAt: new Date(),
      updatedAt: new Date()
    });
    
    console.log('User document created successfully');
  } catch (error) {
    console.error('Error creating user document:', error);
    throw error;
  }
}

/**
 * Upgrade station to station_advertiser
 * @param {string} userId - User ID
 */
export async function upgradeToStationAdvertiser(userId) {
  try {
    const role = await getUserRole(userId);
    
    if (role !== 'station') {
      throw new Error('Only station users can be upgraded to station_advertiser');
    }
    
    await setUserRole(userId, 'station_advertiser');
    console.log(`User ${userId} upgraded to station_advertiser`);
  } catch (error) {
    console.error('Error upgrading to station_advertiser:', error);
    throw error;
  }
}

/**
 * Get role display name
 * @param {string} role - Role key
 * @returns {string} Human-readable role name
 */
export function getRoleDisplayName(role) {
  const names = {
    admin: 'Administrator',
    advertiser: 'Whale Watching Company',
    station: 'Station Operator',
    station_advertiser: 'Station + Advertiser',
  };
  return names[role] || 'Unknown';
}

/**
 * Get role icon
 * @param {string} role - Role key
 * @returns {string} Emoji icon
 */
export function getRoleIcon(role) {
  const icons = {
    admin: '🔐',
    advertiser: '🐋',
    station: '📡',
    station_advertiser: '📡🐋',
  };
  return icons[role] || '👤';
}
